/* HeatWave 3D viewer: dependency-free WebGL balaclava with a thermochromic "breath" effect.
   Colors per variant match the supplier's real before/after photos. */
(function () {
  'use strict';

  var PALETTE = {
    grey:   { cold: '#9d9c99', warm: '#f4f3ef' },
    red:    { cold: '#5d3038', warm: '#ec8b7d' },
    green:  { cold: '#78aba8', warm: '#d5ee90' },
    orange: { cold: '#de7234', warm: '#f6e46c' }
  };

  function toLin(hex) {
    var n = parseInt(hex.slice(1), 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255].map(function (c) { return Math.pow(c, 2.2); });
  }
  function toSrgb(lin) { return lin.map(function (c) { return Math.pow(c, 1 / 2.2); }); }
  function smooth(e0, e1, x) { var t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); }

  /* ---------- tiny matrix helpers (column-major) ---------- */
  function perspective(fovy, aspect, near, far) {
    var f = 1 / Math.tan(fovy / 2), nf = 1 / (near - far);
    return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0]);
  }
  function mul(a, b) {
    var o = new Float32Array(16);
    for (var c = 0; c < 4; c++) for (var r = 0; r < 4; r++) {
      o[c * 4 + r] = a[r] * b[c * 4] + a[4 + r] * b[c * 4 + 1] + a[8 + r] * b[c * 4 + 2] + a[12 + r] * b[c * 4 + 3];
    }
    return o;
  }
  function rotY(a) { var c = Math.cos(a), s = Math.sin(a); return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]); }
  function rotX(a) { var c = Math.cos(a), s = Math.sin(a); return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]); }
  function translate(x, y, z) { return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]); }
  function mat3of(m) { return new Float32Array([m[0], m[1], m[2], m[4], m[5], m[6], m[8], m[9], m[10]]); }

  /* ---------- procedural balaclava mesh ---------- */
  function buildMesh() {
    var prof = [
      [1.30, 0.00], [1.265, 0.27], [1.16, 0.51], [0.99, 0.675], [0.75, 0.775], [0.49, 0.805],
      [0.23, 0.79], [-0.01, 0.735], [-0.23, 0.645], [-0.43, 0.55], [-0.60, 0.505], [-0.76, 0.535],
      [-0.93, 0.67], [-1.08, 0.85], [-1.20, 0.975], [-1.30, 1.04]
    ];
    function sample(v) {
      var t = v * (prof.length - 1), i = Math.min(prof.length - 2, Math.floor(t)), s = t - i;
      var p0 = prof[Math.max(0, i - 1)], p1 = prof[i], p2 = prof[i + 1], p3 = prof[Math.min(prof.length - 1, i + 2)];
      function cr(a, b, c, d) { return 0.5 * ((2 * b) + (-a + c) * s + (2 * a - 5 * b + 4 * c - d) * s * s + (-a + 3 * b - 3 * c + d) * s * s * s); }
      return [cr(p0[0], p1[0], p2[0], p3[0]), Math.max(0, cr(p0[1], p1[1], p2[1], p3[1]))];
    }
    var U = 160, V = 112, cols = U + 1;
    var pos = new Float32Array(cols * (V + 1) * 3), uv = new Float32Array(cols * (V + 1) * 2);
    for (var i = 0; i <= V; i++) {
      var yr = sample(i / V), y0 = yr[0], r0 = yr[1];
      for (var j = 0; j <= U; j++) {
        var th = -Math.PI + (j / U) * Math.PI * 2;
        var r = r0, y = y0;
        var kHead = smooth(-0.9, -0.5, y0);
        var sz = 1 + 0.12 * kHead, sx = 1 - 0.035 * kHead;
        var bib = 1 - smooth(-1.3, -0.82, y0);
        y -= bib * (0.10 * Math.cos(2 * th) + 0.06 * Math.cos(th) + 0.04);
        r += bib * 0.034 * Math.sin(th * 8 + 0.6) * (0.6 + 0.4 * Math.sin(th * 3 + 1.1));
        var dth = th;
        r += 0.085 * Math.exp(-Math.pow(dth / 0.22, 2) - Math.pow((y0 - 0.10) / 0.17, 2)); // nose
        r += 0.035 * Math.exp(-Math.pow(dth / 0.55, 2) - Math.pow((y0 + 0.30) / 0.12, 2)); // chin
        r += 0.010 * Math.sin(y0 * 38) * Math.exp(-Math.pow((y0 + 0.64) / 0.13, 2)) * (0.5 + 0.5 * Math.cos(th)); // neck creases
        var k = (i * cols + j);
        pos[k * 3] = r * sx * Math.sin(th);
        pos[k * 3 + 1] = y;
        pos[k * 3 + 2] = r * sz * Math.cos(th);
        uv[k * 2] = th;
        uv[k * 2 + 1] = y0;
      }
    }
    var idx = new Uint16Array(U * V * 6), n = 0;
    for (i = 0; i < V; i++) for (j = 0; j < U; j++) {
      var a = i * cols + j, b = a + 1, c = a + cols, d = c + 1;
      idx[n++] = a; idx[n++] = c; idx[n++] = b;
      idx[n++] = b; idx[n++] = c; idx[n++] = d;
    }
    var nor = new Float32Array(pos.length);
    for (var t = 0; t < idx.length; t += 3) {
      var i0 = idx[t] * 3, i1 = idx[t + 1] * 3, i2 = idx[t + 2] * 3;
      var ax = pos[i1] - pos[i0], ay = pos[i1 + 1] - pos[i0 + 1], az = pos[i1 + 2] - pos[i0 + 2];
      var bx = pos[i2] - pos[i0], by = pos[i2 + 1] - pos[i0 + 1], bz = pos[i2 + 2] - pos[i0 + 2];
      var nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
      nor[i0] += nx; nor[i0 + 1] += ny; nor[i0 + 2] += nz;
      nor[i1] += nx; nor[i1 + 1] += ny; nor[i1 + 2] += nz;
      nor[i2] += nx; nor[i2 + 1] += ny; nor[i2 + 2] += nz;
    }
    for (i = 0; i <= V; i++) { // weld seam normals at the back
      var s0 = (i * cols) * 3, s1 = (i * cols + U) * 3;
      for (var q = 0; q < 3; q++) { var avg = nor[s0 + q] + nor[s1 + q]; nor[s0 + q] = avg; nor[s1 + q] = avg; }
    }
    for (var m = 0; m < nor.length; m += 3) {
      var L = Math.hypot(nor[m], nor[m + 1], nor[m + 2]) || 1;
      nor[m] /= L; nor[m + 1] /= L; nor[m + 2] /= L;
    }
    for (j = 0; j <= U; j++) { nor[j * 3] = 0; nor[j * 3 + 1] = 1; nor[j * 3 + 2] = 0; } // top pole
    return { pos: pos, nor: nor, uv: uv, idx: idx };
  }

  var VS = [
    'attribute vec3 aPos; attribute vec3 aNor; attribute vec2 aUV;',
    'uniform mat4 uModel; uniform mat4 uVP; uniform mat3 uNorm;',
    'varying vec3 vObj; varying vec3 vW; varying vec3 vN; varying vec2 vUV;',
    'void main(){ vObj=aPos; vUV=aUV; vec4 w=uModel*vec4(aPos,1.0); vW=w.xyz; vN=uNorm*aNor; gl_Position=uVP*w; }'
  ].join('\n');

  var FS = [
    'precision highp float;',
    'varying vec3 vObj; varying vec3 vW; varying vec3 vN; varying vec2 vUV;',
    'uniform vec3 uCold; uniform vec3 uWarm; uniform vec3 uCam; uniform vec3 uMouth;',
    'uniform float uHeat; uniform float uTime;',
    'float hash(vec3 p){ p=fract(p*0.3183099+0.1); p*=17.0; return fract(p.x*p.y*p.z*(p.x+p.y+p.z)); }',
    'float noise(vec3 x){ vec3 i=floor(x); vec3 f=fract(x); f=f*f*(3.0-2.0*f);',
    ' return mix(mix(mix(hash(i),hash(i+vec3(1,0,0)),f.x),mix(hash(i+vec3(0,1,0)),hash(i+vec3(1,1,0)),f.x),f.y),',
    '            mix(mix(hash(i+vec3(0,0,1)),hash(i+vec3(1,0,1)),f.x),mix(hash(i+vec3(0,1,1)),hash(i+vec3(1,1,1)),f.x),f.y),f.z); }',
    'float fbm(vec3 p){ float a=0.5; float s=0.0; for(int i=0;i<4;i++){ s+=a*noise(p); p*=2.03; a*=0.5; } return s; }',
    'void main(){',
    '  float th=vUV.x; float y=vUV.y;',
    '  float ex=th/0.92; float ey=(y-0.45)/0.195;',
    '  float se=pow(abs(ex),3.2)+pow(abs(ey),3.2);',
    '  if(se<1.0) discard;',
    '  vec3 N=normalize(vN); bool inside=!gl_FrontFacing; if(inside) N=-N;',
    '  vec3 d=vObj-uMouth; d.y*= d.y>0.0 ? 0.72 : 1.25;',
    '  float n=fbm(vObj*3.2+vec3(0.0,uTime*0.03,0.0));',
    '  float R=uHeat*0.82;',
    '  float heat=1.0-smoothstep(R-0.26,R+0.02,length(d)+(n-0.5)*0.40);',
    '  heat*=smoothstep(0.0,0.06,uHeat);',
    '  vec3 col=mix(uCold,uWarm,heat);',
    '  col*=(0.95+0.08*noise(vObj*7.0))*(0.97+0.04*noise(vObj*48.0));',
    '  float hem=smoothstep(1.0,1.08,se)*(1.0-smoothstep(1.55,1.75,se));',
    '  col*=1.0-0.07*hem;',
    '  float st=(1.0-smoothstep(0.0,0.05,abs(se-1.30)))*step(0.45,fract(th*26.0));',
    '  float cs=0.0; if(y>0.62){ float dx=abs(abs(vObj.x)-0.19); cs=(1.0-smoothstep(0.004,0.013,dx))*step(0.4,fract(vObj.z*15.0+y*8.0)); }',
    '  float bh=0.0; if(y<-1.22){ bh=(1.0-smoothstep(0.0,0.012,abs(y+1.255)))*step(0.45,fract(th*30.0)); }',
    '  float ps=0.0; float at=abs(th); if(y<0.29 && y>-0.66){ float tl=0.92+(0.29-y)*0.62; ps=(1.0-smoothstep(0.0,0.018,abs(at-tl)))*step(0.45,fract(y*28.0)); col*=1.0-0.06*(1.0-smoothstep(0.0,0.05,at-tl))*step(tl,at); }',
    '  col=mix(col,col*1.22+0.015,max(max(max(st,cs),bh),ps)*0.85);',
    '  if(inside){ col=mix(uCold,vec3(0.02),0.45)*(0.8+0.25*noise(vObj*60.0)); }',
    '  vec3 V=normalize(uCam-vW);',
    '  vec3 L1=normalize(vec3(-0.55,0.65,0.75)); vec3 L2=normalize(vec3(0.8,0.15,0.45)); vec3 L3=normalize(vec3(0.1,0.5,-1.0));',
    '  float w=0.45;',
    '  float d1=max(0.0,(dot(N,L1)+w)/(1.0+w)); float d2=max(0.0,(dot(N,L2)+w)/(1.0+w)); float d3=max(0.0,dot(N,L3));',
    '  vec3 lin=col;',
    '  vec3 amb=mix(vec3(0.10,0.10,0.14),vec3(0.32,0.32,0.38),N.y*0.5+0.5);',
    '  vec3 light=amb+vec3(1.0,0.97,0.92)*d1*1.05+vec3(0.55,0.62,0.85)*d2*0.45;',
    '  float fres=pow(1.0-max(dot(N,V),0.0),2.5);',
    '  vec3 c=lin*light+lin*fres*0.5+vec3(0.9,0.85,1.0)*d3*fres*0.35;',
    '  vec3 H=normalize(L1+V); c+=pow(max(dot(N,H),0.0),24.0)*0.03;',
    '  if(inside) c*=0.42;',
    '  c=c/(1.0+c*0.15);',
    '  gl_FragColor=vec4(pow(c,vec3(1.0/2.2)),1.0);',
    '}'
  ].join('\n');

  var PVS = [
    'attribute vec3 aPos; attribute vec2 aD;',
    'uniform mat4 uModel; uniform mat4 uVP; uniform float uPx;',
    'varying float vA;',
    'void main(){ vec4 p=uVP*uModel*vec4(aPos,1.0); gl_Position=p; gl_PointSize=aD.y*uPx/p.w; vA=aD.x; }'
  ].join('\n');
  var PFS = [
    'precision mediump float; varying float vA;',
    'void main(){ vec2 c=gl_PointCoord-0.5; float r=dot(c,c)*4.0; float a=exp(-r*3.0)*vA; if(a<0.003) discard; gl_FragColor=vec4(vec3(0.93,0.96,1.0)*a,a); }'
  ].join('\n');

  function compile(gl, vs, fs) {
    function sh(type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    var p = gl.createProgram();
    gl.attachShader(p, sh(gl.VERTEX_SHADER, vs)); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    return p;
  }

  function init(root) {
    var canvas = root.querySelector('[data-hw3d-canvas]');
    var breatheBtn = root.querySelector('[data-hw3d-breathe]');
    var toggleBtn = root.querySelector('[data-hw3d-toggle]');
    var fill = root.querySelector('[data-hw3d-fill]');
    var status = root.querySelector('[data-hw3d-status]');
    var swCold = root.querySelector('[data-hw3d-cold]');
    var swWarm = root.querySelector('[data-hw3d-warm]');
    if (!canvas) return;

    var gl = canvas.getContext('webgl', { antialias: true, alpha: true, premultipliedAlpha: true });
    if (!gl) return;

    var prog, pprog;
    try { prog = compile(gl, VS, FS); pprog = compile(gl, PVS, PFS); } catch (e) { return; }

    var mesh = buildMesh();
    function buf(target, data) { var b = gl.createBuffer(); gl.bindBuffer(target, b); gl.bufferData(target, data, gl.STATIC_DRAW); return b; }
    var bPos = buf(gl.ARRAY_BUFFER, mesh.pos), bNor = buf(gl.ARRAY_BUFFER, mesh.nor), bUV = buf(gl.ARRAY_BUFFER, mesh.uv);
    var bIdx = buf(gl.ELEMENT_ARRAY_BUFFER, mesh.idx);
    var loc = {
      aPos: gl.getAttribLocation(prog, 'aPos'), aNor: gl.getAttribLocation(prog, 'aNor'), aUV: gl.getAttribLocation(prog, 'aUV')
    };
    var u = {};
    ['uModel', 'uVP', 'uNorm', 'uCold', 'uWarm', 'uCam', 'uMouth', 'uHeat', 'uTime'].forEach(function (k) { u[k] = gl.getUniformLocation(prog, k); });
    var pl = { aPos: gl.getAttribLocation(pprog, 'aPos'), aD: gl.getAttribLocation(pprog, 'aD') };
    var pu = { uModel: gl.getUniformLocation(pprog, 'uModel'), uVP: gl.getUniformLocation(pprog, 'uVP'), uPx: gl.getUniformLocation(pprog, 'uPx') };

    var MAXP = 90, parts = [];
    var pPosArr = new Float32Array(MAXP * 3), pDArr = new Float32Array(MAXP * 2);
    var bPP = gl.createBuffer(), bPD = gl.createBuffer();

    /* ---------- state ---------- */
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var yaw = 0.55, pitch = 0.06, velYaw = 0, dragging = false, lastX = 0, lastY = 0, lastInteract = -10;
    var focusFront = false, breathing = false, breathStart = 0, heat = 0, time = 0, spawnAcc = 0;
    var cold = toLin(PALETTE.grey.cold), warm = toLin(PALETTE.grey.warm), coldT = cold.slice(), warmT = warm.slice();
    var mouth = [0, -0.04, 0.80];
    var visible = true, running = false, lastT = 0, demoDone = false;
    var FOV = 30 * Math.PI / 180;

    function setVariant(value) {
      var key = null, v = String(value || '').toLowerCase();
      Object.keys(PALETTE).forEach(function (k) { if (v.indexOf(k) !== -1) key = k; });
      if (!key) return;
      coldT = toLin(PALETTE[key].cold); warmT = toLin(PALETTE[key].warm);
      if (swCold) swCold.style.background = PALETTE[key].cold;
      if (swWarm) swWarm.style.background = PALETTE[key].warm;
      kick();
    }
    var form = document.querySelector('[data-product-form]');
    var photo = root.querySelector('.hw3d-photo');
    var variantData = [];
    try { variantData = JSON.parse(form.querySelector('[data-variants-json]').textContent); } catch (err) {}
    function readVariant() {
      if (!form) return;
      var checked = form.querySelectorAll('[data-option-index]:checked');
      var val = Array.prototype.map.call(checked, function (i) { return i.value; }).join(' ');
      setVariant(val);
      var idInput = form.querySelector('[name="id"]');
      if (photo && idInput) {
        var v = variantData.filter(function (x) { return String(x.id) === String(idInput.value); })[0];
        if (v && v.image && photo.getAttribute('src') !== v.image) photo.setAttribute('src', v.image);
      }
    }
    if (form) form.addEventListener('change', readVariant);
    readVariant();
    cold = coldT.slice(); warm = warmT.slice();

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.max(1, Math.round(canvas.clientWidth * dpr)), h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    }

    /* ---------- input ---------- */
    canvas.addEventListener('pointerdown', function (e) {
      dragging = true; focusFront = false; lastX = e.clientX; lastY = e.clientY; velYaw = 0; lastInteract = time;
      root.classList.add('hw3d--touched');
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      kick();
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY; lastX = e.clientX; lastY = e.clientY;
      var k = 4.2 / Math.max(240, canvas.clientWidth);
      yaw += dx * k; velYaw = dx * k * 60;
      if (e.pointerType === 'mouse') pitch = Math.max(-0.35, Math.min(0.4, pitch + dy * k * 0.6));
      lastInteract = time;
    });
    function endDrag() { dragging = false; lastInteract = time; }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    function startBreath() {
      if (breathing) return;
      breathing = true; breathStart = time; focusFront = true; lastInteract = time;
      root.classList.add('hw3d--breathing');
      if (breatheBtn) breatheBtn.setAttribute('aria-pressed', 'true');
      kick();
    }
    var stopTimer = null;
    function stopBreath() {
      if (!breathing) return;
      var held = time - breathStart, minHold = 1.1;
      clearTimeout(stopTimer);
      stopTimer = setTimeout(function () {
        breathing = false; lastInteract = time;
        root.classList.remove('hw3d--breathing');
        if (breatheBtn) breatheBtn.setAttribute('aria-pressed', 'false');
      }, Math.max(0, (minHold - held) * 1000));
    }
    if (breatheBtn) {
      breatheBtn.addEventListener('pointerdown', function (e) { e.preventDefault(); try { breatheBtn.setPointerCapture(e.pointerId); } catch (err) {} startBreath(); });
      breatheBtn.addEventListener('pointerup', stopBreath);
      breatheBtn.addEventListener('pointercancel', stopBreath);
      breatheBtn.addEventListener('keydown', function (e) { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); startBreath(); } });
      breatheBtn.addEventListener('keyup', function (e) { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); stopBreath(); } });
      breatheBtn.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    }
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function () {
        var photo = root.classList.toggle('hw3d--photo');
        toggleBtn.textContent = photo ? '3D view' : 'Photo';
        toggleBtn.setAttribute('aria-pressed', photo ? 'true' : 'false');
        if (!photo) kick();
      });
    }

    /* ---------- particles ---------- */
    function spawn() {
      if (parts.length >= MAXP) return;
      parts.push({
        x: mouth[0] + (Math.random() - 0.5) * 0.10, y: mouth[1] + (Math.random() - 0.5) * 0.06, z: mouth[2] + 0.05,
        vx: (Math.random() - 0.5) * 0.30, vy: 0.10 + Math.random() * 0.10, vz: 0.55 + Math.random() * 0.30,
        age: 0, life: 1.4 + Math.random() * 0.6, s0: 0.07 + Math.random() * 0.05
      });
    }

    /* ---------- render loop ---------- */
    function frame(now) {
      if (!visible || document.hidden || root.classList.contains('hw3d--photo')) { running = false; return; }
      var dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 0.016; lastT = now; time += dt;
      resize();

      // rotation
      if (!dragging) {
        if (focusFront) {
          var target = Math.round(yaw / (Math.PI * 2)) * Math.PI * 2;
          yaw += (target - yaw) * Math.min(1, dt * 4); velYaw = 0;
          pitch += (0.04 - pitch) * Math.min(1, dt * 4);
          if (!breathing && heat < 0.02) focusFront = false;
        } else {
          yaw += velYaw * dt; velYaw *= Math.pow(0.04, dt);
          if (!reduce && time - lastInteract > 2.5) yaw += dt * 0.35;
        }
      }
      // heat dynamics: warms fast while breathing, cools slowly like the real pigment
      if (breathing) heat = Math.min(1, heat + dt * 0.6); else heat = Math.max(0, heat - dt * 0.24);
      for (var q = 0; q < 3; q++) { cold[q] += (coldT[q] - cold[q]) * Math.min(1, dt * 6); warm[q] += (warmT[q] - warm[q]) * Math.min(1, dt * 6); }

      if (breathing) { spawnAcc += dt * 26; while (spawnAcc > 1) { spawn(); spawnAcc--; } }
      for (var p = parts.length - 1; p >= 0; p--) {
        var P = parts[p]; P.age += dt;
        if (P.age > P.life) { parts.splice(p, 1); continue; }
        P.x += P.vx * dt; P.y += P.vy * dt; P.z += P.vz * dt;
        P.vx *= Math.pow(0.5, dt); P.vz *= Math.pow(0.45, dt); P.vy += dt * 0.05;
      }

      if (fill) fill.style.transform = 'scaleX(' + heat.toFixed(3) + ')';
      if (status) {
        var s = breathing ? 'Warming up…' : (heat > 0.04 ? 'Cooling down…' : 'Cold');
        if (status.textContent !== s) status.textContent = s;
      }

      var W = canvas.width, H = canvas.height;
      gl.viewport(0, 0, W, H);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);
      gl.disable(gl.CULL_FACE);

      var aspect = W / H;
      var dist = aspect < 0.7 ? 8.9 : 7.9;
      var proj = perspective(FOV, aspect, 0.1, 50);
      var view = translate(0, 0.30, -dist);
      var vp = mul(proj, view);
      var model = mul(rotX(pitch), rotY(yaw));
      // camera position in world space (view is a pure translation)
      var cam = [0, -0.30, dist];

      gl.useProgram(prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, bPos); gl.enableVertexAttribArray(loc.aPos); gl.vertexAttribPointer(loc.aPos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, bNor); gl.enableVertexAttribArray(loc.aNor); gl.vertexAttribPointer(loc.aNor, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, bUV); gl.enableVertexAttribArray(loc.aUV); gl.vertexAttribPointer(loc.aUV, 2, gl.FLOAT, false, 0, 0);
      gl.uniformMatrix4fv(u.uModel, false, model);
      gl.uniformMatrix4fv(u.uVP, false, vp);
      gl.uniformMatrix3fv(u.uNorm, false, mat3of(model));
      gl.uniform3fv(u.uCold, cold); gl.uniform3fv(u.uWarm, warm);
      gl.uniform3fv(u.uCam, cam); gl.uniform3fv(u.uMouth, mouth);
      gl.uniform1f(u.uHeat, heat); gl.uniform1f(u.uTime, time);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bIdx);
      gl.drawElements(gl.TRIANGLES, mesh.idx.length, gl.UNSIGNED_SHORT, 0);
      gl.disableVertexAttribArray(loc.aNor); gl.disableVertexAttribArray(loc.aUV);

      if (parts.length) {
        for (var i = 0; i < parts.length; i++) {
          var Q = parts[i], t = Q.age / Q.life;
          pPosArr[i * 3] = Q.x; pPosArr[i * 3 + 1] = Q.y; pPosArr[i * 3 + 2] = Q.z;
          pDArr[i * 2] = 0.12 * Math.pow(Math.sin(Math.PI * t), 1.4);
          pDArr[i * 2 + 1] = Q.s0 + t * 0.6;
        }
        gl.useProgram(pprog);
        gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.depthMask(false);
        gl.bindBuffer(gl.ARRAY_BUFFER, bPP); gl.bufferData(gl.ARRAY_BUFFER, pPosArr.subarray(0, parts.length * 3), gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(pl.aPos); gl.vertexAttribPointer(pl.aPos, 3, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, bPD); gl.bufferData(gl.ARRAY_BUFFER, pDArr.subarray(0, parts.length * 2), gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(pl.aD); gl.vertexAttribPointer(pl.aD, 2, gl.FLOAT, false, 0, 0);
        gl.uniformMatrix4fv(pu.uModel, false, model);
        gl.uniformMatrix4fv(pu.uVP, false, vp);
        gl.uniform1f(pu.uPx, H / (2 * Math.tan(FOV / 2)));
        gl.drawArrays(gl.POINTS, 0, parts.length);
        gl.disableVertexAttribArray(pl.aD);
        gl.depthMask(true); gl.disable(gl.BLEND);
      }

      requestAnimationFrame(frame);
    }
    function kick() { if (!running && visible && !document.hidden) { running = true; lastT = 0; requestAnimationFrame(frame); } }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) {
          kick();
          if (!demoDone) { // one short automatic breath so visitors see the effect immediately
            demoDone = true;
            setTimeout(function () { if (!breathing && heat < 0.02) { startBreath(); setTimeout(stopBreath, 1500); } }, 900);
          }
        }
      }, { threshold: 0.25 }).observe(root);
    }
    document.addEventListener('visibilitychange', kick);
    window.addEventListener('resize', kick);

    root.classList.add('hw3d--ready');
    root.__hw3d = { get heat() { return heat; }, startBreath: startBreath, stopBreath: stopBreath, setVariant: setVariant, setYaw: function (a) { yaw = a; velYaw = 0; lastInteract = time; } };
    kick();
  }

  function boot() { document.querySelectorAll('[data-hw3d]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
